import { useCallback } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { fireErrorAnalytics, fireSiteOptedOutAnalytics } from '../../services/analytics/index.tsx';
import {
	getOrUpdateFeatureVisited,
	getFeatureVisitedCount,
	withEligibilityCache,
	cjnEligibilityCacheHandler,
} from '../../services/caching/index.tsx';
import { useFilteredUsersFromCollaborationGraph } from '../../services/get-collaborators/index.tsx';
import { useNudgeStateV2a } from '../../services/nudge-state/index.tsx';
import { hasProductAccess } from '../../services/permission-check/index.tsx';
import { fetchIsRequestAccessCapable } from '../../services/request-capabilities/index.tsx';
import { isSite7DaysOld } from '../../services/site-eligibility/index.tsx';
import { isSiteOptedOut } from '../../services/site-opt-out/index.tsx';
import { generateCJNContent } from '../../services/generated-content/index.tsx';
import { isNewActiveUser } from '../../services/user-eligibility/index.tsx';
import { MAX_FEATURE_VISIT_COUNT } from '../nudges/common.tsx';
import { useOverrides } from '../../services/overrides/index.tsx';
import type { EligibilityCheck } from './types.tsx';

const MAX_NUMBER_OF_CONFLUENCE_COLLABORATORS = 5;

/*
  Eligibility can be overriden using the following query parameter:

  ?cjn_conf_overrides={
    "is_your_work_route": false,
    "site_has_no_licensed_confluence": false,
    "feature_visited_too_recently": false,
    "site_is_opted_out": false,
    "user_already_has_confluence": false,
    "user_cannot_request_confluence_access": false,
    "confluence_is_too_new": false,
    "user_is_too_new": false,
    "nudge_already_dismissed": false,
    "nudge_seen_too_many_times": false
  }
*/
type OverrideKey =
	| 'is_your_work_route'
	| 'site_has_no_licensed_confluence'
	| 'feature_visited_too_recently'
	| 'site_is_opted_out'
	| 'user_already_has_confluence'
	| 'user_cannot_request_confluence_access'
	| 'confluence_is_too_new'
	| 'user_is_too_new'
	| 'nudge_already_dismissed'
	| 'nudge_seen_too_many_times';

export const useConfluenceCrossJoinNudgeEligibility = () => {
	const cloudId = useCloudId();
	const getFilteredCollaborators = useFilteredUsersFromCollaborationGraph(
		cloudId,
		'confluence',
		MAX_NUMBER_OF_CONFLUENCE_COLLABORATORS,
	);
	const { getNudgeState: getNudgeStateV2a } = useNudgeStateV2a();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const userId = useAccountId() || '';
	const licensedProducts = useLicensedProducts();
	const isYourWorkRoute = useIsActiveRoute('your-work');
	const locale = useLocale();
	const withOverrides = useOverrides<OverrideKey>('cjn_conf_overrides');

	return useCallback(async (): Promise<EligibilityCheck<{ featureVisitedCount: number }>> => {
		const startTimestamp = Date.now();
		try {
			const cachedIsSiteOptedOut = withEligibilityCache('site_is_opted_out', isSiteOptedOut, 0.2);
			const cachedHasProductAccess = withEligibilityCache(
				'user_already_has_confluence',
				hasProductAccess,
				0.2,
			);
			const cachedUserCanRequestAccess = withEligibilityCache(
				'user_cannot_request_confluence_access',
				fetchIsRequestAccessCapable,
				0.2,
			);

			if (withOverrides('is_your_work_route', isYourWorkRoute)) {
				return { isEligible: false, ineligibleReason: 'is_your_work_route' };
			}

			if (withOverrides('site_has_no_licensed_confluence', !licensedProducts.confluence)) {
				return { isEligible: false, ineligibleReason: 'site_has_no_licensed_confluence' };
			}

			if (
				withOverrides(
					'feature_visited_too_recently',
					getOrUpdateFeatureVisited('crossJoinNudgesConfluence', 30),
				)
			) {
				return { isEligible: false, ineligibleReason: 'feature_visited_too_recently' };
			}

			if (withOverrides('site_is_opted_out', await cachedIsSiteOptedOut(cloudId))) {
				fireSiteOptedOutAnalytics(createAnalyticsEvent, 'crossJoinNudgesConfluence');
				return { isEligible: false, ineligibleReason: 'site_is_opted_out' };
			}

			const userHasProductAccess = await cachedHasProductAccess('confluence', cloudId);
			if (withOverrides('user_already_has_confluence', userHasProductAccess)) {
				return { isEligible: false, ineligibleReason: 'user_already_has_confluence' };
			}

			const userCanRequestAccessToConfluence = await cachedUserCanRequestAccess(
				'confluence',
				cloudId,
			);
			if (
				withOverrides('user_cannot_request_confluence_access', !userCanRequestAccessToConfluence)
			) {
				return { isEligible: false, ineligibleReason: 'user_cannot_request_confluence_access' };
			}

			if (withOverrides('confluence_is_too_new', !(await isSite7DaysOld('confluence', cloudId)))) {
				return { isEligible: false, ineligibleReason: 'confluence_is_too_new' };
			}

			if (withOverrides('user_is_too_new', await isNewActiveUser(cloudId, userId))) {
				return { isEligible: false, ineligibleReason: 'user_is_too_new' };
			}

			// Since V2A Rollout to 100%, all nudge will use V2A Logic
			if (withOverrides('nudge_already_dismissed', (await getNudgeStateV2a()) === 'DISMISSED')) {
				return { isEligible: false, ineligibleReason: 'nudge_already_dismissed' };
			}

			const featureVisitedCount = await getFeatureVisitedCount(userId);
			const isEnglishLocale = locale && locale.startsWith('en');

			// NOTE: This AA test shouldn't be run in parallel with the AB 'grw-1755-growth-exp-jira-cjn-llm' experiment. Ensure flags are properly coordinated in statsig.
			if (isEnglishLocale && fg('grw-1755-growth-exp-jira-cjn-llm-aa')) {
				(async function () {
					try {
						if (!getOrUpdateFeatureVisited('crossJoinNudgesLLMAA', 7)) {
							const collaborators = await getFilteredCollaborators();
							const hasNoCollaborators = collaborators.length === 0;
							if (!hasNoCollaborators) {
								// user is part of v2a
								return;
							}
							await generateCJNContent();
							fireTrackAnalytics(createAnalyticsEvent({}), 'feature exposed', {
								flagKey: 'grw-1755-growth-exp-jira-cjn-llm-aa',
								featureVisitedCount,
								msSinceStart: startTimestamp - Date.now(),
							});
						}
					} catch (error: unknown) {
						// We're running an AA test, so there's no user-facing error handling
						// The error is already captured in analytics
					}
				})();
			}

			if (
				withOverrides('nudge_seen_too_many_times', featureVisitedCount >= MAX_FEATURE_VISIT_COUNT)
			) {
				return { isEligible: false, ineligibleReason: 'nudge_seen_too_many_times' };
			}

			return { isEligible: true, data: { featureVisitedCount } };

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics('useCrossJoinNudges', error, { cloudId });
			return { isEligible: false, ineligibleReason: 'unexpected error' };
		}
	}, [
		cloudId,
		createAnalyticsEvent,
		getFilteredCollaborators,
		getNudgeStateV2a,
		isYourWorkRoute,
		licensedProducts.confluence,
		locale,
		userId,
		withOverrides,
	]);
};

/**
 * @function useLlmConfluenceCrossJoinNudgeEligibility approximates eligibility to the LLM experiment
 * in order to assist with deconflicting user groups from other experiments.
 * To fully test whether a user should see a confluence nudge at a given time,
 * the `useConfluenceCrossJoinNudgeEligibility` function should still be used instead
 */
export const useLlmConfluenceCrossJoinNudgeEligibility = () => {
	const cloudId = useCloudId();
	const getFilteredCollaborators = useFilteredUsersFromCollaborationGraph(
		cloudId,
		'confluence',
		MAX_NUMBER_OF_CONFLUENCE_COLLABORATORS,
	);
	const locale = useLocale();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const licensedProducts = useLicensedProducts();
	const withOverrides = useOverrides<OverrideKey>('cjn_conf_overrides');

	return useCallback(async (): Promise<EligibilityCheck> => {
		try {
			const cachedIsSiteOptedOut = withEligibilityCache('site_is_opted_out', isSiteOptedOut, 0.2);
			const cachedHasProductAccess = withEligibilityCache(
				'user_already_has_confluence',
				hasProductAccess,
				0.2,
			);
			const cachedUserCanRequestAccess = withEligibilityCache(
				'user_cannot_request_confluence_access',
				fetchIsRequestAccessCapable,
				0.2,
			);

			async function hasCollaborators(): Promise<boolean> {
				const collaborators = (await getFilteredCollaborators()).length ?? 0;
				return collaborators > 0;
			}

			const hasCollaboratorsWithCache = withEligibilityCache(
				'has_collaborators',
				hasCollaborators,
				7,
			);

			const isEnglishLocale = locale && locale.startsWith('en');
			if (!isEnglishLocale) {
				return { isEligible: false, ineligibleReason: 'not_english_locale' };
			}

			if (withOverrides('site_has_no_licensed_confluence', !licensedProducts.confluence)) {
				return { isEligible: false, ineligibleReason: 'site_has_no_licensed_confluence' };
			}

			if (withOverrides('site_is_opted_out', await cachedIsSiteOptedOut(cloudId))) {
				fireSiteOptedOutAnalytics(createAnalyticsEvent, 'crossJoinNudgesConfluence');
				return { isEligible: false, ineligibleReason: 'site_is_opted_out' };
			}

			const userHasProductAccess = await cachedHasProductAccess('confluence', cloudId);
			if (withOverrides('user_already_has_confluence', userHasProductAccess)) {
				return { isEligible: false, ineligibleReason: 'user_already_has_confluence' };
			}

			const userCanRequestAccessToConfluence = await cachedUserCanRequestAccess(
				'confluence',
				cloudId,
			);
			if (
				withOverrides('user_cannot_request_confluence_access', !userCanRequestAccessToConfluence)
			) {
				return { isEligible: false, ineligibleReason: 'user_cannot_request_confluence_access' };
			}

			if (await hasCollaboratorsWithCache()) {
				return { isEligible: false, ineligibleReason: 'has_collaborators' };
			}

			return { isEligible: true, data: {} };

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics('useCrossJoinNudges', error, { cloudId });
			return { isEligible: false, ineligibleReason: 'unexpected error' };
		}
	}, [
		cloudId,
		getFilteredCollaborators,
		locale,
		createAnalyticsEvent,
		licensedProducts.confluence,
		withOverrides,
	]);
};

export const useHasSeenConfLlm = () => {
	return useCallback(() => {
		return cjnEligibilityCacheHandler.get('has_seen_conf_llm');
	}, []);
};
